/**
 * Teams List Route
 *
 * Displays searchable, filterable list of teams with navigation
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useBulkOperations } from '../../hooks/useBulkOperations';
import { useTeams } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  BulkActionToolbar,
  BulkDeleteConfirmationDialog,
  BulkUpdateDialog,
  type DataTableColumn,
  type BulkUpdateFieldConfig,
} from '../../components/shared';
import type { Team, TeamType } from '../../graphql/types';
import { bulkDeleteTeams, bulkUpdateTeams } from '../../lib/apiClient';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/teams/')({
  component: TeamsListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { TeamsListPage };

interface TeamFilters {
  search: string;
  type: TeamType | '';
}

const TEAM_BULK_FIELDS: BulkUpdateFieldConfig[] = [
  {
    key: 'type',
    label: 'Team Type',
    type: 'select',
    options: [
      { value: 'development', label: 'Development' },
      { value: 'operations', label: 'Operations' },
      { value: 'platform', label: 'Platform' },
      { value: 'security', label: 'Security' },
      { value: 'data', label: 'Data' },
    ],
  },
];

const ForgeButton = 'forge-button' as React.ElementType;

function TeamsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<TeamFilters>({
    search: '',
    type: '',
  });

  // Fetch teams with GraphQL
  const { data, loading, error } = useTeams();

  // Extract teams from connection
  const teams = data?.teams?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, filteredItems, pagedItems, totalCount } =
    useListPageState<Team>({
      items: teams,
      page,
      pageSize,
      filterFn: (team) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = team.name.toLowerCase().includes(searchLower);
          const matchesDescription = team.description?.toLowerCase().includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.type && team.type !== filters.type) {
          return false;
        }
        return true;
      },
      sortValue: (team, column) => String(team[column as keyof Team] ?? ''),
    });

  const bulk = useBulkOperations<Team, Parameters<typeof bulkUpdateTeams>[1]>({
    rows: filteredItems,
    getRowId: (team) => team.entityId,
    getRowName: (team) => team.name,
    bulkDelete: (ids) => bulkDeleteTeams(ids),
    bulkUpdate: (ids, updates) => bulkUpdateTeams(ids, updates),
    refetchQueries: ['ListTeams'],
    exportFileName: 'teams-export',
  });

  // DataTable columns
  const columns: DataTableColumn<Team>[] = [
    {
      key: 'name',
      label: 'Team Name',
      sortable: true,
      width: '25%',
      render: (team) => (
        <div>
          <div className="teams-name-cell">{team.name}</div>
          {team.description && (
            <div className="teams-description-cell">
              {team.description.substring(0, 60)}
              {team.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: '15%',
      render: (team) => <span className="teams-type-cell">{team.type}</span>,
    },
    {
      key: 'memberIds',
      label: 'Members',
      width: '10%',
      render: (team) => (
        <span className="page-header-subtitle">{team.memberIds.length} members</span>
      ),
    },
    {
      key: 'contactEmail',
      label: 'Contact',
      width: '20%',
      render: (team) => <div className="teams-truncate-cell">{team.contactEmail}</div>,
    },
    {
      key: 'teamsChannel',
      label: 'Teams',
      width: '15%',
      render: (team) => (
        <div className="teams-truncate-cell">
          {team.teamsChannel ? (
            <a href={team.teamsChannel} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            '—'
          )}
        </div>
      ),
    },
  ];

  const handleRowClick = (team: Team): void => {
    navigate({ to: `/teams/${team.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<TeamFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/teams', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', type: '' });
    void navigate({ to: '/teams', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/teams', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({ to: '/teams', search: { page: 1, pageSize: newPageSize } });
  };

  if (error) {
    return (
      <div className="teams-list-page">
        <h1 className="forge-typography--headline4">Teams</h1>
        <div className="teams-error-state">
          <EmptyState
            icon="error"
            title="Failed to load teams"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="teams-list-page">
      <div className="teams-header">
        <div>
          <h1 className="forge-typography--headline4">Teams</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} teams`}
          </p>
        </div>
        <PermissionGate permission="team:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/teams/new' })}
          >
            Create Team
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search teams..."
            aria-label="Search teams"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by team type"
          value={filters.type}
          onChange={(e) => handleFilterChange({ type: e.target.value as TeamType | '' })}
        >
          <option value="">All Types</option>
          <option value="development">Development</option>
          <option value="operations">Operations</option>
          <option value="platform">Platform</option>
          <option value="security">Security</option>
          <option value="data">Data</option>
        </select>

        {(filters.search || filters.type) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Bulk Action Toolbar */}
      {bulk.selectedIds.size > 0 && (
        <BulkActionToolbar
          selectedCount={bulk.selectedIds.size}
          onClearSelection={bulk.clearSelection}
          onBulkDelete={() => bulk.setBulkDeleteOpen(true)}
          onBulkUpdate={() => bulk.setBulkUpdateOpen(true)}
          onBulkExport={bulk.handleBulkExport}
        />
      )}

      {/* Data Table */}
      <div className="teams-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={5} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(team) => team.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              selectable
              selectedIds={bulk.selectedIds}
              onSelectionChange={bulk.setSelectedIds}
              emptyState={{
                icon: 'inbox',
                title: 'No teams found',
                description:
                  filters.search || filters.type
                    ? 'Try adjusting your filters'
                    : 'Create your first team to get started',
                actionLabel: 'Create Team',
                onAction: () => navigate({ to: '/teams/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>

      {/* Bulk Delete Dialog */}
      <BulkDeleteConfirmationDialog
        open={bulk.bulkDeleteOpen}
        itemNames={bulk.selectedNames}
        onConfirm={() => void bulk.handleBulkDelete()}
        onCancel={() => bulk.setBulkDeleteOpen(false)}
        isDeleting={bulk.isBulkDeleting}
        result={bulk.bulkDeleteResult}
        onDone={bulk.handleBulkDeleteDone}
      />

      {/* Bulk Update Dialog */}
      <BulkUpdateDialog
        open={bulk.bulkUpdateOpen}
        selectedCount={bulk.selectedIds.size}
        entityLabel={{ singular: 'team', plural: 'teams' }}
        fields={TEAM_BULK_FIELDS}
        idPrefix="bulk-team"
        selectClassName="filter-select"
        onConfirm={(updates) =>
          void bulk.handleBulkUpdate(updates as Parameters<typeof bulkUpdateTeams>[1])
        }
        onCancel={() => bulk.setBulkUpdateOpen(false)}
        isUpdating={bulk.isBulkUpdating}
      />
    </div>
  );
}
